import { useEffect, useMemo, useState } from "react";
import {
  getAdminFolders,
  createAdminFolder,
  updateAdminFolder,
  deleteAdminFolder,
  getAdminPracticeVideos,
  updateAdminPracticeVideo,
} from "../../services/adminService";
import CloudinaryImageUpload from "../utilPages/CloudinaryImageUpload";
import { getVideoThumbnail } from "../../utils/videoThumbnail";

const ROLES = ["basic", "studio", "onlineClub"];
const NON_CATALOG_PATHS = ["/blog", "/library/lives", "/library/courses"];

const emptyForm = {
  path: "",
  nameRu: "",
  nameEn: "",
  image: "",
  allowedRoles: [],
  order: 0,
  visible: true,
  hideFilters: false,
};

const mapFolderToForm = (folder) => ({
  path: folder.path || "",
  nameRu: folder.name?.ru || "",
  nameEn: folder.name?.en || "",
  image: folder.image || "",
  allowedRoles: Array.isArray(folder.allowedRoles) ? folder.allowedRoles : [],
  order: folder.order ?? 0,
  visible: folder.visible !== false,
  hideFilters: folder.hideFilters === true,
});

const ChevronIcon = ({ open }) => (
  <svg
    className={`w-4 h-4 shrink-0 text-gray-400 transition-transform duration-200 ${open ? "rotate-90" : ""}`}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
  </svg>
);

const CheckIcon = () => (
  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
  </svg>
);

const Pill = ({ children, tone = "gray" }) => {
  const tones = {
    gray: "bg-gray-100 text-gray-600",
    green: "bg-green-50 text-green-700 ring-1 ring-inset ring-green-200",
    amber: "bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-200",
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${tones[tone]}`}>
      {children}
    </span>
  );
};

// Small labeled section used to break the edit form into scannable groups.
const Section = ({ title, description, children, first = false }) => (
  <div className={first ? "" : "pt-5 border-t border-gray-100"}>
    <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-400">{title}</h4>
    {description && <p className="text-xs text-gray-400 mt-0.5 mb-3">{description}</p>}
    <div className={description ? "" : "mt-3"}>{children}</div>
  </div>
);

const ToggleSwitch = ({ checked, onChange, label, description }) => (
  <div className="flex items-start gap-3 w-fit">
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`mt-0.5 relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brown focus-visible:ring-offset-2 ${
        checked ? "bg-brown" : "bg-gray-200"
      }`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
          checked ? "translate-x-6" : "translate-x-1"
        }`}
      />
    </button>
    <span>
      <span className="block text-sm font-medium text-gray-700">{label}</span>
      {description && <span className="block text-xs text-gray-400">{description}</span>}
    </span>
  </div>
);

const RolePicker = ({ selected, onToggle }) => (
  <div>
    <div className="flex flex-wrap gap-2">
      {ROLES.map((role) => {
        const active = selected.includes(role);
        return (
          <button
            key={role}
            type="button"
            onClick={() => onToggle(role)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${
              active
                ? "bg-brown text-white border-brown shadow-sm"
                : "bg-white text-gray-600 border-gray-300 hover:border-brown hover:text-brown"
            }`}
          >
            {role}
          </button>
        );
      })}
    </div>
    <p className="text-xs text-gray-400 mt-2">
      {selected.length === 0 ? "Visible to everyone" : `Visible only to: ${selected.join(", ")}`}
    </p>
  </div>
);

const VideoAssignmentList = ({ folderId, practices, onToggle }) => {
  const [search, setSearch] = useState("");
  const [assignedOnly, setAssignedOnly] = useState(false);
  const [pendingIds, setPendingIds] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const isAssigned = (practice) => (practice.folderIds || []).includes(folderId);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return practices.filter((practice) => {
      if (assignedOnly && !isAssigned(practice)) return false;
      if (!term) return true;
      const haystack = [practice.contentType, ...(practice.tags || []), ...(practice.level || [])]
        .join(" ")
        .toLowerCase();
      return haystack.includes(term);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [practices, search, assignedOnly, folderId]);

  const assignedCount = practices.filter(isAssigned).length;

  const setPending = (id, isPending) => {
    setPendingIds((prev) => {
      const next = new Set(prev);
      if (isPending) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const handleToggle = async (practice) => {
    setPending(practice._id, true);
    try {
      await onToggle(practice._id, !isAssigned(practice));
    } finally {
      setPending(practice._id, false);
    }
  };

  const handleBulk = async (shouldAssign) => {
    const targets = filtered.filter((practice) => isAssigned(practice) !== shouldAssign);
    if (targets.length === 0) return;
    setBulkBusy(true);
    try {
      await Promise.all(targets.map((practice) => onToggle(practice._id, shouldAssign)));
    } finally {
      setBulkBusy(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-3">
        <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-400">Videos in this folder</h4>
        <Pill tone={assignedCount > 0 ? "green" : "gray"}>{assignedCount} assigned</Pill>
      </div>

      <div className="flex flex-col sm:flex-row gap-2 mb-3">
        <input
          className="flex-1 border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:ring-2 focus:ring-brown focus:border-brown"
          placeholder="Filter by tag, level, or type..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={() => setAssignedOnly((v) => !v)}
            className={`px-3 py-1.5 rounded-md text-xs font-medium border transition whitespace-nowrap ${
              assignedOnly
                ? "bg-brown text-white border-brown"
                : "bg-white text-gray-600 border-gray-300 hover:border-brown hover:text-brown"
            }`}
          >
            Assigned only
          </button>
          <button
            type="button"
            onClick={() => handleBulk(true)}
            disabled={bulkBusy}
            className="px-3 py-1.5 rounded-md text-xs font-medium border border-gray-300 text-gray-600 hover:border-brown hover:text-brown transition disabled:opacity-50 whitespace-nowrap"
          >
            Select shown
          </button>
          <button
            type="button"
            onClick={() => handleBulk(false)}
            disabled={bulkBusy}
            className="px-3 py-1.5 rounded-md text-xs font-medium border border-gray-300 text-gray-600 hover:border-red-300 hover:text-red-600 transition disabled:opacity-50 whitespace-nowrap"
          >
            Clear shown
          </button>
        </div>
      </div>

      <div className="max-h-72 overflow-y-auto rounded-lg border border-gray-200 divide-y divide-gray-100 bg-gray-50/50">
        {filtered.length === 0 && (
          <p className="text-sm text-gray-400 px-3 py-6 text-center">No videos match.</p>
        )}
        {filtered.map((practice) => {
          const checked = isAssigned(practice);
          const pending = pendingIds.has(practice._id);
          const primaryUrl = practice.videos?.ru?.default || practice.videos?.en?.default || practice.videoURL || "";
          const thumb = practice.coverPhotos?.ru || practice.coverPhotos?.en || getVideoThumbnail(primaryUrl);
          return (
            <label
              key={practice._id}
              className={`flex items-center gap-3 px-3 py-2 text-sm cursor-pointer transition ${
                checked ? "bg-brown/5" : "bg-white hover:bg-gray-50"
              } ${pending ? "opacity-50" : ""}`}
            >
              <span className="relative shrink-0">
                {thumb ? (
                  <img src={thumb} alt="" className="w-10 h-10 rounded-md object-cover bg-gray-100" />
                ) : (
                  <div className="w-10 h-10 rounded-md bg-gray-100" />
                )}
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={pending}
                  onChange={() => handleToggle(practice)}
                  className="sr-only"
                />
                <span
                  className={`absolute -top-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center border-2 border-white transition ${
                    checked ? "bg-brown text-white" : "bg-gray-200 text-transparent"
                  }`}
                >
                  <CheckIcon />
                </span>
              </span>
              <span className="text-gray-700 truncate">
                <span className="font-medium">{practice.duration || 0} min</span>
                <span className="text-gray-400"> · {practice.contentType === "lecture" ? "lecture" : "practice"}</span>
                {practice.tags?.length > 0 && (
                  <span className="text-gray-400"> · {practice.tags.slice(0, 3).join(", ")}</span>
                )}
              </span>
            </label>
          );
        })}
      </div>
    </div>
  );
};

const FolderRow = ({
  folder,
  token,
  isNew = false,
  startExpanded = false,
  practices,
  onSaved,
  onCreated,
  onDeleted,
  onDiscard,
  onToggleVideo,
}) => {
  const [expanded, setExpanded] = useState(startExpanded);
  const [form, setForm] = useState(() => (isNew ? emptyForm : mapFolderToForm(folder)));
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const [langTab, setLangTab] = useState("ru");

  const isCatalogFolder = isNew || !NON_CATALOG_PATHS.includes(folder?.path);
  const showVideos = !isNew && isCatalogFolder;

  const toggleRole = (role) => {
    setSaved(false);
    setForm((prev) => ({
      ...prev,
      allowedRoles: prev.allowedRoles.includes(role)
        ? prev.allowedRoles.filter((r) => r !== role)
        : [...prev.allowedRoles, role],
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError("");
    const payload = {
      path: form.path,
      name: { ru: form.nameRu, en: form.nameEn },
      image: form.image,
      allowedRoles: form.allowedRoles,
      order: Number(form.order) || 0,
      visible: form.visible,
      hideFilters: form.hideFilters,
    };
    try {
      if (isNew) {
        const created = await createAdminFolder(token, payload);
        onCreated(created);
      } else {
        const updated = await updateAdminFolder(token, folder._id, payload);
        onSaved(updated);
        setSaved(true);
      }
    } catch (err) {
      setError(err.message || "Failed to save folder");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete the "${form.nameEn || form.path}" folder? This cannot be undone.`)) return;
    setDeleting(true);
    setError("");
    try {
      await deleteAdminFolder(token, folder._id);
      onDeleted(folder._id);
    } catch (err) {
      setError(err.message || "Failed to delete folder");
      setDeleting(false);
    }
  };

  const roleSummary = form.allowedRoles.length === 0 ? "Everyone" : form.allowedRoles.join(", ");

  return (
    <div
      className={`border rounded-xl overflow-hidden bg-white transition-shadow ${
        expanded ? "border-brown/30 shadow-sm" : "border-gray-200"
      }`}
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 transition"
      >
        <ChevronIcon open={expanded} />
        {form.image ? (
          <img src={form.image} alt="" className="w-10 h-10 rounded-md object-cover bg-gray-100 shrink-0" />
        ) : (
          <div className="w-10 h-10 rounded-md bg-gray-100 shrink-0" />
        )}
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-gray-800 truncate">
            {form.nameEn || form.nameRu || (isNew ? "New folder" : form.path)}
          </p>
          <p className="text-xs text-gray-400 truncate font-mono">{form.path || "no path set"}</p>
        </div>
        <div className="hidden sm:flex items-center gap-1.5 shrink-0">
          <Pill tone={form.allowedRoles.length === 0 ? "green" : "amber"}>{roleSummary}</Pill>
          {!form.visible && <Pill>Hidden</Pill>}
        </div>
      </button>

      {expanded && (
        <div className="border-t border-gray-100 px-4 sm:px-6 py-5 text-left">
          <div className={showVideos ? "grid grid-cols-1 lg:grid-cols-2 gap-8" : ""}>
            <div className="space-y-0">
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-400">Name</h4>
                  <div className="inline-flex rounded-md border border-gray-200 bg-gray-50 p-0.5">
                    <button
                      type="button"
                      onClick={() => setLangTab("ru")}
                      className={`px-3 py-1 text-xs font-semibold rounded transition ${
                        langTab === "ru" ? "bg-white shadow-sm text-gray-900" : "text-gray-500 hover:text-gray-700"
                      }`}
                    >
                      RU
                    </button>
                    <button
                      type="button"
                      onClick={() => setLangTab("en")}
                      className={`px-3 py-1 text-xs font-semibold rounded transition ${
                        langTab === "en" ? "bg-white shadow-sm text-gray-900" : "text-gray-500 hover:text-gray-700"
                      }`}
                    >
                      EN
                    </button>
                  </div>
                </div>
                <div className="max-w-sm">
                  <label className="block text-xs text-gray-500 mb-1.5">{langTab === "ru" ? "Russian" : "English"}</label>
                  <input
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-brown focus:border-brown"
                    value={langTab === "ru" ? form.nameRu : form.nameEn}
                    onChange={(e) => {
                      setSaved(false);
                      const field = langTab === "ru" ? "nameRu" : "nameEn";
                      setForm((p) => ({ ...p, [field]: e.target.value }));
                    }}
                  />
                </div>
              </div>

              <Section title="Path" description="Use /library/<name> to get a page that lists whichever videos you assign below.">
                <input
                  className="w-full max-w-sm border border-gray-300 rounded-md px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-brown focus:border-brown"
                  placeholder="/library/workouts"
                  value={form.path}
                  onChange={(e) => {
                    setSaved(false);
                    setForm((p) => ({ ...p, path: e.target.value }));
                  }}
                />
              </Section>

              <Section title="Tile Image">
                <div className="max-w-xs">
                  <CloudinaryImageUpload
                    value={form.image}
                    onChange={(url) => {
                      setSaved(false);
                      setForm((p) => ({ ...p, image: url }));
                    }}
                    token={token}
                    folder="onceUpOnAMat/folderTiles"
                  />
                </div>
              </Section>

              <Section title="Access">
                <RolePicker selected={form.allowedRoles} onToggle={toggleRole} />
              </Section>

              <Section title="Display">
                <div className="space-y-4">
                  <div className="max-w-xs">
                    <label className="block text-xs text-gray-500 mb-1.5">Order</label>
                    <input
                      type="number"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-brown focus:border-brown"
                      value={form.order}
                      onChange={(e) => {
                        setSaved(false);
                        setForm((p) => ({ ...p, order: e.target.value }));
                      }}
                    />
                  </div>
                  <ToggleSwitch
                    label="Visible on Library page"
                    checked={form.visible}
                    onChange={(v) => {
                      setSaved(false);
                      setForm((p) => ({ ...p, visible: v }));
                    }}
                  />
                  <ToggleSwitch
                    label="Hide level/type/duration filters"
                    description="Useful for folders with a small, uniform set of videos"
                    checked={form.hideFilters}
                    onChange={(v) => {
                      setSaved(false);
                      setForm((p) => ({ ...p, hideFilters: v }));
                    }}
                  />
                </div>
              </Section>

              {error && <p className="text-sm text-red-600 pt-4">{error}</p>}

              <div className="flex items-center gap-3 pt-5 border-t border-gray-100 mt-5">
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving || deleting}
                  className="px-4 py-2 text-sm font-medium rounded-md bg-brown text-white shadow-sm hover:opacity-90 disabled:opacity-50 transition"
                >
                  {saving ? "Saving..." : isNew ? "Create" : "Save"}
                </button>
                {isNew ? (
                  <button
                    type="button"
                    onClick={onDiscard}
                    disabled={saving}
                    className="px-4 py-2 text-sm font-medium rounded-md border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50 transition"
                  >
                    Discard
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleDelete}
                    disabled={saving || deleting}
                    className="px-4 py-2 text-sm font-medium rounded-md border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-50 transition"
                  >
                    {deleting ? "Deleting..." : "Delete"}
                  </button>
                )}
                {saved && <span className="text-sm text-green-600">Saved</span>}
              </div>
            </div>

            {showVideos && (
              <div className="pt-5 border-t border-gray-100 lg:pt-0 lg:border-t-0 lg:border-l lg:pl-8 mt-5 lg:mt-0">
                <VideoAssignmentList
                  folderId={folder._id}
                  practices={practices}
                  onToggle={(practiceId, checked) => onToggleVideo(folder._id, practiceId, checked)}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const AdminContentFolders = ({ token }) => {
  const [folders, setFolders] = useState([]);
  const [practices, setPractices] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [nextDraftId, setNextDraftId] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    Promise.all([getAdminFolders(token), getAdminPracticeVideos(token)])
      .then(([foldersData, practicesData]) => {
        setFolders(foldersData);
        setPractices(practicesData);
      })
      .catch((err) => setError(err.message || "Failed to load folders"))
      .finally(() => setLoading(false));
  }, [token]);

  const handleSaved = (updated) => {
    setFolders((prev) => prev.map((f) => (f._id === updated._id ? updated : f)));
  };

  const handleDeleted = (id) => {
    setFolders((prev) => prev.filter((f) => f._id !== id));
  };

  const handleAddDraft = () => {
    setDrafts((prev) => [...prev, nextDraftId]);
    setNextDraftId((id) => id + 1);
  };

  const handleCreated = (draftId, created) => {
    setDrafts((prev) => prev.filter((id) => id !== draftId));
    setFolders((prev) => [...prev, created]);
  };

  const handleDiscard = (draftId) => {
    setDrafts((prev) => prev.filter((id) => id !== draftId));
  };

  const handleToggleVideo = async (folderId, practiceId, checked) => {
    const practice = practices.find((p) => p._id === practiceId);
    if (!practice) return;
    const currentFolderIds = practice.folderIds || [];
    const nextFolderIds = checked
      ? [...currentFolderIds, folderId]
      : currentFolderIds.filter((id) => id !== folderId);
    const updated = await updateAdminPracticeVideo(token, practiceId, { folderIds: nextFolderIds });
    setPractices((prev) => prev.map((p) => (p._id === practiceId ? updated : p)));
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 space-y-4 text-left">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-800">Library Folders</h2>
          <p className="text-sm text-gray-600 mt-1">
            Manage which Library tiles are shown, who can see them, and which videos appear inside each one.
          </p>
        </div>
        <button
          type="button"
          onClick={handleAddDraft}
          className="shrink-0 px-4 py-2 text-sm font-medium rounded-md bg-brown text-white shadow-sm hover:opacity-90 transition"
        >
          + Add Folder
        </button>
      </div>

      {loading && <p className="text-sm text-gray-500">Loading folders...</p>}
      {error && <p className="text-sm text-red-600">{error}</p>}

      <div className="space-y-3">
        {drafts.map((draftId) => (
          <FolderRow
            key={`draft-${draftId}`}
            folder={null}
            token={token}
            isNew
            startExpanded
            practices={practices}
            onCreated={(created) => handleCreated(draftId, created)}
            onDiscard={() => handleDiscard(draftId)}
          />
        ))}
        {folders.map((folder) => (
          <FolderRow
            key={folder._id}
            folder={folder}
            token={token}
            practices={practices}
            onSaved={handleSaved}
            onDeleted={handleDeleted}
            onToggleVideo={handleToggleVideo}
          />
        ))}
      </div>
    </div>
  );
};

export default AdminContentFolders;
